import React from 'react';
import { X, Trash2, CheckCircle, AlertOctagon } from 'lucide-react';
import { useGraphStore } from '../../store/graphStore';
import { Button } from '../ui/Button';

export function DetailPanel() {
    const {
        selectedNodeId, selectedEdgeId,
        data,
        updateNode, removeNode,
        updateEdge, removeEdge,
        selectNode, selectEdge
    } = useGraphStore();

    const selectedNode = React.useMemo(() =>
        data.nodes.find(n => n.id === selectedNodeId),
        [data.nodes, selectedNodeId]);

    const selectedEdge = React.useMemo(() =>
        data.edges.find(e => e.id === selectedEdgeId),
        [data.edges, selectedEdgeId]);

    const closePanel = () => {
        selectNode(null);
        selectEdge(null);
    };

    if (!selectedNode && !selectedEdge) return null;

    return (
        <div className="absolute top-4 right-4 w-80 bg-surface/95 backdrop-blur-xl border border-border rounded-xl shadow-2xl flex flex-col z-20 overflow-hidden animate-in slide-in-from-right-10 fade-in duration-200">

            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-surface-hover/30">
                <h2 className="font-bold text-lg">
                    {selectedNode ? 'Edit Node' : 'Edit Connection'}
                </h2>
                <Button variant="ghost" size="icon" onClick={closePanel}>
                    <X className="h-4 w-4" />
                </Button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">

                {/* Node Form */}
                {selectedNode && (
                    <>
                        <div className="space-y-1">
                            <label className="text-xs text-text-muted font-medium uppercase">Type</label>
                            <div className="text-sm font-mono bg-background/50 p-2 rounded border border-border">
                                {selectedNode.type}
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs text-text-muted font-medium uppercase">Name</label>
                            <input
                                type="text"
                                value={selectedNode.name}
                                onChange={(e) => updateNode(selectedNode.id, { name: e.target.value })}
                                className="w-full bg-background border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs text-text-muted font-medium uppercase">Notes</label>
                            <textarea
                                value={selectedNode.notes || ''}
                                onChange={(e) => updateNode(selectedNode.id, { notes: e.target.value })}
                                className="w-full bg-background border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary h-24 resize-none"
                            />
                        </div>

                        <div className="space-y-2 pt-2 border-t border-border">
                            <label className="text-xs text-text-muted font-medium uppercase">Security Checks</label>
                            <div className="flex flex-col gap-2">
                                <label className="flex items-center gap-2 text-sm cursor-pointer select-none group">
                                    <input
                                        type="checkbox"
                                        checked={!!selectedNode.security_checks?.two_factor_enabled}
                                        onChange={(e) => updateNode(selectedNode.id, {
                                            security_checks: { ...selectedNode.security_checks, two_factor_enabled: e.target.checked }
                                        })}
                                        className="w-4 h-4 rounded border-border bg-background text-primary focus:ring-primary"
                                    />
                                    <span className="group-hover:text-primary transition-colors">2FA Enabled</span>
                                </label>

                                <label className="flex items-center gap-2 text-sm cursor-pointer select-none group">
                                    <input
                                        type="checkbox"
                                        checked={!!selectedNode.security_checks?.hardware_backed}
                                        onChange={(e) => updateNode(selectedNode.id, {
                                            security_checks: { ...selectedNode.security_checks, hardware_backed: e.target.checked }
                                        })}
                                        className="w-4 h-4 rounded border-border bg-background text-primary focus:ring-primary"
                                    />
                                    <span className="group-hover:text-primary transition-colors">Hardware Backed</span>
                                </label>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-border">
                            <Button variant="danger" size="sm" className="w-full gap-2" onClick={() => removeNode(selectedNode.id)}>
                                <Trash2 className="h-4 w-4" />
                                Delete Node
                            </Button>
                        </div>
                    </>
                )}

                {/* Edge Form */}
                {selectedEdge && (
                    <>
                        <div className="space-y-1">
                            <label className="text-xs text-text-muted font-medium uppercase">Relation Type</label>
                            <select
                                value={selectedEdge.type}
                                onChange={(e) => updateEdge(selectedEdge.id, { type: e.target.value as any })}
                                className="w-full bg-background border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                                <option value="SecuredBy">Secured By</option>
                                <option value="RecoveredBy">Recovered By</option>
                                <option value="ContainedIn">Contained In</option>
                                <option value="OwnedBy">Owned By</option>
                                <option value="DependsOn">Depends On</option>
                            </select>
                        </div>

                        <div className="pt-4 border-t border-border">
                            <Button variant="danger" size="sm" className="w-full gap-2" onClick={() => removeEdge(selectedEdge.id)}>
                                <Trash2 className="h-4 w-4" />
                                Remove Connection
                            </Button>
                        </div>
                    </>
                )}

            </div>
        </div>
    );
}
